import axios from 'axios';

// 创建axios实例
// 优先使用构建期注入的 REACT_APP_API_BASE_URL；否则走相对路径 /api
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器
apiClient.interceptors.request.use(
  (config) => {
    console.log('API请求:', config.method?.toUpperCase(), config.url);
    return config;
  },
  (error) => {
    console.error('API请求错误:', error);
    return Promise.reject(error);
  }
);

// 响应拦截器
apiClient.interceptors.response.use(
  (response) => {
    console.log('API响应:', response.status, response.config.url);
    return response;
  },
  (error) => {
    console.error('API响应错误:', error.response?.status, error.message);
    
    // 统一错误处理
    if (error.response?.status === 500) {
      console.error('服务器内部错误');
    } else if (error.response?.status === 404) {
      console.error('接口不存在');
    } else if (error.code === 'ECONNABORTED') {
      console.error('请求超时');
    } else if (!error.response) {
      console.error('网络连接错误');
    }
    
    return Promise.reject(error);
  }
);

// 动态基址：
// 1) 构建变量 REACT_APP_API_BASE_URL 优先
// 2) 如在本地 dev (端口 3000) 则默认 http://127.0.0.1:3001/api
// 3) 否则使用相对路径 /api 交给 nginx 反代
let dynamicBase = process.env.REACT_APP_API_BASE_URL;
if (!dynamicBase) {
  if (typeof window !== 'undefined' && window.location.port === '3000') {
    dynamicBase = 'http://127.0.0.1:3001/api';
  } else {
    dynamicBase = '/api';
  }
}
const BASE = dynamicBase.replace(/\/+$/, '');

export const uploadAPI = {
  async uploadImage(file) {
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(`${BASE}/upload-image`, {
      method: 'POST',
      body: form,
    });
    if (!res.ok) throw new Error(`upload ${res.status}`);
    return res.json();
  }
};

// 聊天相关API
export const chatAPI = {
  // 非流式（保留）
  sendMessage: (message, conversationHistory = []) => apiClient.post('/chat/message', { message, conversationHistory }),

  // 统一流式（文本 / 图文）
  async streamMessage({ text, history = [], imageUrl, imageFile, onToken, signal }) {
    const endpoint = `${BASE}/chat/stream`;
    let resp;
    try { console.debug('[stream:start]', { endpoint, hasImage: !!(imageFile || imageUrl) }); } catch {}
    if (imageFile || imageUrl) {
      const form = new FormData();
      form.append('message', text || '');
      form.append('conversationHistory', JSON.stringify(history));
      if (imageFile) form.append('file', imageFile);
      if (imageUrl) form.append('image_url', imageUrl);
      resp = await fetch(endpoint, { method: 'POST', body: form, signal });
    } else {
      // 不显式设置 Content-Type，避免触发 CORS 预检（OPTIONS）
      resp = await fetch(endpoint, {
        method: 'POST',
        body: JSON.stringify({ message: text || '', conversationHistory: history }),
        signal,
        cache: 'no-store'
      });
    }
    try { console.debug('[stream:resp]', { ok: resp.ok, status: resp.status, ct: resp.headers.get('content-type') }); } catch {}
    if (!resp.ok) throw new Error(`stream ${resp.status}`);
    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let receivedAny = false;
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      try { console.debug('[stream:chunk]', { len: value?.length }); } catch {}
      buffer += decoder.decode(value, { stream: true });
      try { console.debug('[stream:decoded]', buffer.slice(0, 120)); } catch {}
      // 以双换行为块分隔（兼容 CRLF）
      const parts = buffer.split(/\r?\n\r?\n/);
      buffer = parts.pop() || '';
      for (const part of parts) {
        const trimmed = part.trim();
        if (!trimmed) continue;
        if (trimmed === '[DONE]') return;
        if (trimmed === '\u200b') continue;
        receivedAny = true;
        // 保持自然段落与列表，由渲染层决定换行
        // 轻量格式化：为常见中文小节标题添加换行，提升可读性
        let formatted = trimmed;
        const headingHints = [
          '主要特点：', '适合人群：', '下一步建议',
          '分阶段培养：', '案例驱动：', '导师制：', '数字化融合：', '职业发展支持：'
        ];
        // 避免在加粗标记内插入换行，破坏 Markdown（如 **主要特点：**）
        if (!/\*\*/.test(formatted)) {
          headingHints.forEach(h => {
            formatted = formatted.replace(new RegExp(`${h}`), `${h}\n`);
          });
        }
        try { console.debug('[stream:emit]', formatted.slice(0, 120)); } catch {}
        // 关键：每个块末尾补双换行，保持与后端一致
        onToken && onToken(formatted + "\n\n");
      }
    }
    // 处理尾部未分隔的剩余内容
    const tail = (buffer || '').trim();
    if (tail && tail !== '[DONE]') {
      receivedAny = true;
      try { console.debug('[stream:emit-tail]', tail.slice(0, 120)); } catch {}
      onToken && onToken(tail + "\n\n");
    }
    try { console.debug('[stream:end]', { receivedAny, tailLen: tail.length }); } catch {}
    if (!receivedAny) {
      onToken && onToken('（未收到模型输出，可能网络中断或 KEY/部署配置异常）');
    }
  }
};

// 医生相关API
export const doctorAPI = {
  // 获取医生档案
  getProfile: () => {
    return apiClient.get('/doctor/profile');
  },
  
  // 获取专业领域
  getExpertise: () => {
    return apiClient.get('/doctor/expertise');
  },
  
  // 获取在线状态
  getStatus: () => {
    return apiClient.get('/doctor/status');
  }
};

// 健康检查API
export const healthAPI = {
  check: () => {
    return apiClient.get('/health');
  }
};

export default apiClient;
